import { SLACK_TS_PATTERN } from "./constants";
import { AdminMessage, Message } from "./types";

const parseSlackTs = (ts: string): [number, number] | null => {
  const match = SLACK_TS_PATTERN.exec(ts);
  if (!match) return null;
  return [Number(match[1]), Number(match[2])];
};

/**
 * Whether one Slack `ts` is later than another, compared as seconds then
 * microseconds so no precision is lost to a float.
 *
 * @param ts - The timestamp in question.
 * @param other - The one to compare against.
 * @returns True only when both parse and `ts` is strictly later.
 */
export const isSlackTsLater = (ts: string, other: string): boolean => {
  const a = parseSlackTs(ts);
  const b = parseSlackTs(other);
  if (!a || !b) return false;
  return a[0] > b[0] || (a[0] === b[0] && a[1] > b[1]);
};

/**
 * Inserts a mirrored admin reply in Slack `ts` order. The backend can hold an
 * image reply until its upload finishes, so a later text reply may arrive
 * first; the incoming reply moves above the trailing admin replies whose ts is
 * later than its own. User messages carry no ts, so it never moves above one,
 * and an equal or unparseable ts keeps arrival order.
 *
 * @param messages - The current thread, oldest first.
 * @param incoming - The admin reply that just arrived.
 * @returns A new thread array with `incoming` inserted.
 */
export const insertAdminMessage = (messages: Message[], incoming: AdminMessage): Message[] => {
  let index = messages.length;
  while (index > 0) {
    const previous = messages[index - 1];
    if (previous.kind !== "admin" || !isSlackTsLater(previous.ts, incoming.ts)) break;
    index -= 1;
  }
  return [...messages.slice(0, index), incoming, ...messages.slice(index)];
};
